// procedureExpansion — expand a chosen CIFP procedure into PlanPoints
// for insertion into the flight plan's route array.
//
// A procedure picker yields:
//   - the procedure (SID/STAR/IAP)
//   - the user's chosen runway transition (e.g. 'RW10L')
//   - the user's chosen enroute transition (e.g. 'INYOE' for an arrival)
//
// We expand into the union of the relevant transition + common segment,
// in the correct sequence:
//
//   SID    : runway transition  →  common (omit IF from common if it
//                                    matches the last leg of runway)
//   STAR   : enroute transition →  common (omit IF from common similarly)
//   IAP    : enroute transition →  common (omit IF from common similarly)
//
// Each PlanPoint produced gets kind='procedure' with a procedureTag
// recording which procedure / transition it came from. Legs without
// resolvable lat/lon (altitude-terminator, manual-terminator) become
// placeholder PlanPoints with the ident only — they show up in the
// list so the pilot sees what's there, but they're not used for the
// map line or distance math.

import type { Procedure, ProcedureLeg, Transition, LegType } from './cifp/cifp';
import { classifyLeg } from './cifp/cifp';
import type { PlanPoint } from '../stores/planStore';
import {
  synthesizeAltitudeTerminator,
  parseTargetAltitudeFt,
  isAltitudeTerminatorLeg,
  isSynthesizableAltitudeTerminator,
} from './altitudeTerminatorSynthesis';

export interface ProcedureTag {
  airportIcao: string;
  procIdent: string;
  routeType: 'SID' | 'STAR' | 'IAP';
  /** Transition ident the pilot picked (runway or enroute trans). */
  transIdent: string;
  /** Leg type (TF/DF/CF/...) for diagnostics & rendering. */
  legType: LegType;
  /** Sequence number from CIFP for stable ordering. */
  seq: number;
  /** True when this leg has no positional fix and was kept as a placeholder. */
  placeholder: boolean;
  /** True when this leg's coordinates were synthesized (e.g. VA/CA/FA
   *  altitude-terminator legs projected from heading + climb perf). */
  synthetic?: boolean;
  /** True when this leg should be hidden from the plan-list UI (e.g.
   *  synthesized altitude-terminator legs that exist only to drive the
   *  map's climb-line geometry). The MapView / legs() still see the
   *  point so distance + bearing math works; the PlanPanel filters it
   *  out so the pilot doesn't see a row labeled "VA@3443ft". */
  hiddenInPlanList?: boolean;
  /** True when this leg was manually added by the pilot (e.g. a hold
   *  inserted via the Garmin-style 'hold at waypoint' dialog or an ATC
   *  hold-as-published instruction). When true, the plan renderer ALWAYS
   *  draws this hold/PT regardless of the course-reversal-required
   *  heuristic. */
  manuallyAdded?: boolean;
  /** Optional altitude constraint from the CIFP record. */
  altitude: string | null;
  /** Optional secondary altitude (used by the 'B' between constraint). */
  altitude2?: string | null;
  /** True for IAP legs that are part of the missed-approach segment
   *  (the MAP itself + everything after). Rendered dashed magenta. */
  isMissedApproach?: boolean;
  /** True for legs marking a holding pattern (hold-in-lieu-of-PT,
   *  missed-approach hold). Rendered as a racetrack symbol. */
  isHoldingPattern?: boolean;
  /** Hold inbound course in degrees (when this leg defines a hold). */
  holdInboundCourse?: number | null;
  /** Hold outbound leg length. */
  holdLeg?: { kind: 'time'; minutes: number } | { kind: 'distance'; nm: number } | null;
  /** Hold turn direction ('L' / 'R'). */
  holdTurn?: 'L' | 'R' | null;
  /** RF (radius-to-fix) center lat/lon, joined at parse time. The arc
   *  has the leg's FROM-point as its start and the leg's fix as its
   *  end; the center plus turn direction fully determine the arc. */
  rfCenterLat?: number | null;
  rfCenterLon?: number | null;
  /** True when this point is the Final Approach Fix (Maltese cross). */
  isFaf?: boolean;
  /** True when this point is the Missed Approach Point. */
  isMap?: boolean;
  /** True when this point is an Initial Approach Fix. */
  isIaf?: boolean;
  /** True when this point is a stepdown fix. */
  isStepdownFix?: boolean;
  /** True when this point is a fly-over fix. */
  isFlyOver?: boolean;
  /** Altitude constraint type for the label glyph:
   *  '+' bar above, '-' bar below, 'B' bar both, 'at' / 'recommended' / null. */
  altitudeConstraint?: '+' | '-' | 'B' | 'at' | 'recommended' | null;
}

export interface ExtendedPlanPoint extends PlanPoint {
  /** When this point came from a CIFP procedure expansion. Read-only:
   *  the planStore preserves this on serialize but doesn't generate it. */
  procedureTag?: ProcedureTag;
}

/** Expand a SID (departure procedure) into PlanPoints. */
export function expandSid(
  proc: Procedure,
  runwayIdent: string,
  enrouteIdent?: string,
  origin?: { lat: number; lon: number; elevFt?: number },
): ExtendedPlanPoint[] {
  const order: Transition[] = [];
  const runway = proc.transitions.find((t) => t.ident === runwayIdent);
  if (runway) order.push(runway);
  const common = proc.transitions.find((t) => t.kind === 'common');
  if (common) order.push(common);
  if (enrouteIdent) {
    const enr = proc.transitions.find((t) => t.ident === enrouteIdent);
    if (enr && !order.includes(enr)) order.push(enr);
  }
  // Seed the altitude-terminator cursor with the departure airport so a
  // SID that starts with a VA/CA/FA leg (heading-to-altitude) can be
  // synthesized instead of becoming a placeholder. Without this seed, the
  // first VA leg has no start lat/lon and gets dropped from the route
  // line, leaving an apparent gap from the airport to the first real fix.
  const initialCursor: Cursor = origin
    ? { cursorLat: origin.lat, cursorLon: origin.lon, cursorAltFt: origin.elevFt ?? null }
    : { cursorLat: null, cursorLon: null, cursorAltFt: null };
  return legsToPlanPoints(order, proc, runwayIdent, initialCursor);
}

/** Expand a STAR (arrival) into PlanPoints. */
export function expandStar(
  proc: Procedure,
  enrouteIdent: string,
  runwayIdent?: string,
): ExtendedPlanPoint[] {
  const order: Transition[] = [];
  const enr = proc.transitions.find((t) => t.ident === enrouteIdent);
  if (enr) order.push(enr);
  const common = proc.transitions.find((t) => t.kind === 'common');
  if (common) order.push(common);
  if (runwayIdent) {
    const rw = proc.transitions.find((t) => t.ident === runwayIdent);
    if (rw && !order.includes(rw)) order.push(rw);
  }
  return legsToPlanPoints(order, proc, enrouteIdent);
}

/** Expand an IAP (instrument approach) into PlanPoints. */
export function expandIap(
  proc: Procedure,
  transIdent: string,
): ExtendedPlanPoint[] {
  const order: Transition[] = [];
  const trans = proc.transitions.find((t) => t.ident === transIdent);
  if (trans) order.push(trans);
  const common = proc.transitions.find((t) => t.kind === 'common');
  if (common && !order.includes(common)) order.push(common);
  return legsToPlanPoints(order, proc, transIdent);
}

// ── Internals ────────────────────────────────────────────────────────────

function legsToPlanPoints(
  transitions: Transition[],
  proc: Procedure,
  chosenTransIdent: string,
  initialCursor: Cursor = { cursorLat: null, cursorLon: null, cursorAltFt: null },
): ExtendedPlanPoint[] {
  const out: ExtendedPlanPoint[] = [];
  const seenFixes = new Set<string>();
  let firstTransitionDone = false;

  // Running cursor for altitude-terminator synthesis. SID callers pass
  // in the departure airport's lat/lon/elev so a VA-first SID synthesizes
  // correctly from the runway threshold. STAR/IAP callers leave it null;
  // altitude-terminator legs on those procedures aren't common as the
  // first leg, and when they are, the IF/CF leg in the same transition
  // provides the cursor seed.
  let cursorLat: number | null = initialCursor.cursorLat;
  let cursorLon: number | null = initialCursor.cursorLon;
  let cursorAltFt: number | null = initialCursor.cursorAltFt;

  // Missed-approach propagation runs INLINE here (not as a post-pass)
  // so we can use the CIFP source legs to detect the boundary even
  // when an altitude-terminator (CA/VA/FA) trigger leg is dropped from
  // the emitted output. (Before 2026-05-17 the trigger legs were
  // emitted as hidden rows so a post-pass could walk `out[]`; now they
  // never reach `out[]` and the post-pass would miss the signal.)
  const MISSED_ONLY_LEGTYPES = new Set(['CA', 'VA', 'FA', 'VM', 'VI']);
  const isIap = proc.routeType === 'IAP';
  let inMissedSegment = false;
  let pendingMissedAfterThis = false;

  for (const t of transitions) {
    let firstOfThisTransition = true;
    for (const leg of t.legs) {
      // Drop the leading IF when joining successive transitions — it's
      // the same fix as the last point of the prior transition.
      if (firstOfThisTransition && firstTransitionDone && leg.legType === 'IF') {
        firstOfThisTransition = false;
        continue;
      }
      firstOfThisTransition = false;

      // Inline missed-approach tracking. We evaluate the boundary BEFORE
      // emitting the point so the emitted tag carries the correct value.
      // The transitions we honor (IAP only):
      //   1. Source leg's isMap=true — stays inbound, schedules the
      //      NEXT leg into missed segment.
      //   2. Source leg's isMissedApproach=true (CIFP byte) AND the leg
      //      is not a MAP — flips the segment for this + later legs.
      //   3. Source leg type is one of the missed-only legtypes
      //      (CA/VA/FA/VM/VI) AND we're in IAP context — same flip.
      //      Even though those legs are DROPPED from the output, we
      //      still see them in `t.legs` and use them as a signal.
      let legIsMissed = false;
      if (isIap) {
        if (pendingMissedAfterThis) {
          inMissedSegment = true;
          pendingMissedAfterThis = false;
        }
        if (leg.isMap === true) {
          // MAP itself stays inbound; promote next leg.
          legIsMissed = false;
          pendingMissedAfterThis = true;
        } else if (leg.isMissedApproach === true) {
          inMissedSegment = true;
          legIsMissed = true;
        } else if (MISSED_ONLY_LEGTYPES.has(leg.legType)) {
          inMissedSegment = true;
          legIsMissed = true;
        } else {
          legIsMissed = inMissedSegment;
        }
      }

      const point = legToPlanPoint(leg, proc, chosenTransIdent, {
        cursorLat, cursorLon, cursorAltFt,
      });
      if (!point) continue;
      // Stamp the inline-tracked missed flag onto the emitted point.
      // Overrides whatever the source leg's CIFP byte said — the
      // inline walker accounts for the MAP-stays-inbound rule that the
      // raw byte misses.
      if (point.procedureTag && isIap) {
        point.procedureTag.isMissedApproach = legIsMissed;
      }

      // Suppress consecutive duplicates by ident+lat+lon. Holding-pattern
      // legs (HM/HF/HA) are exempt because they're semantically different
      // from a TF/DF that arrives at the same fix: the hold says 'orbit
      // here', the TF says 'fly here'. Without this exception, a CIFP
      // sequence like … → DF SAMIE → HM SAMIE (which is how missed-
      // approach holds are typically encoded — fly to the fix, then
      // hold) drops the HM leg entirely.
      const isHoldLeg =
        leg.legType === 'HM' || leg.legType === 'HF' || leg.legType === 'HA';
      if (!isHoldLeg) {
        const key = `${point.ident}|${point.lat?.toFixed(4)}|${point.lon?.toFixed(4)}`;
        if (seenFixes.has(key)) continue;
        seenFixes.add(key);
      }

      out.push(point);

      // Advance the cursor when the point has real coords.
      if (!point.procedureTag?.placeholder) {
        cursorLat = point.lat;
        cursorLon = point.lon;
      }
      // Update altitude from the leg's altitude1 constraint when present.
      const targetAlt = parseTargetAltitudeFt(leg.altitude1);
      if (targetAlt != null) cursorAltFt = targetAlt;
    }
    firstTransitionDone = true;
  }

  // SID / STAR safety pass: clear any stray isMissedApproach byte that
  // slipped through the parser (these segments don't have a missed
  // approach in real life; a leftover byte would render the leg as
  // dashed-magenta on the map).
  if (!isIap) {
    for (const p of out) {
      const tag = p.procedureTag;
      if (tag?.isMissedApproach) tag.isMissedApproach = false;
    }
  }

  return out;
}

interface Cursor {
  cursorLat: number | null;
  cursorLon: number | null;
  cursorAltFt: number | null;
}

function legToPlanPoint(
  leg: ProcedureLeg,
  proc: Procedure,
  chosenTransIdent: string,
  cursor: Cursor = { cursorLat: null, cursorLon: null, cursorAltFt: null },
): ExtendedPlanPoint | null {
  const klass = classifyLeg(leg.legType);
  const ident = leg.fixIdent || `${leg.legType}.${leg.seq}`;

  // A leg is renderable as a holding pattern when its leg type is
  // HM/HF/HA OR when the waypoint description marks it as a hold-in-lieu.
  const isHold = leg.legType === 'HM' || leg.legType === 'HF' || leg.legType === 'HA'
    || leg.isHoldingPattern;

  const baseTag: ProcedureTag = {
    airportIcao: proc.airportIcao,
    procIdent: proc.procIdent,
    routeType: proc.routeType,
    transIdent: chosenTransIdent,
    legType: leg.legType,
    seq: leg.seq,
    placeholder: false,
    altitude: leg.altitude1,
    altitude2: leg.altitude2,
    isMissedApproach: leg.isMissedApproach,
    isHoldingPattern: isHold,
    holdInboundCourse: leg.holdInboundCourse,
    holdLeg: leg.holdLeg,
    holdTurn: leg.turnDirection,
    rfCenterLat: leg.rfCenterLat,
    rfCenterLon: leg.rfCenterLon,
    isFaf: leg.isFaf,
    isMap: leg.isMap,
    isIaf: leg.isIaf,
    isStepdownFix: leg.isStepdownFix,
    isFlyOver: leg.isFlyOver,
    altitudeConstraint: leg.altitudeConstraint,
  };

  if (klass === 'positional' && leg.fixLat != null && leg.fixLon != null) {
    return {
      ident,
      lat: leg.fixLat,
      lon: leg.fixLon,
      kind: 'procedure',
      procedureTag: baseTag,
    };
  }

  // Altitude-terminator legs (VA/CA/FA/VI/CI/CR/VM) used to be emitted
  // as either a synthesized positional point (with `hiddenInPlanList:
  // true` so the plan list ignored it) OR as a (0,0) placeholder. Boss
  // kept seeing the synthesized rows leak through to various surfaces
  // (PlanPanel list, map dots, FlightBriefing route string, AI brief
  // payload, drag-and-drop sequencing) despite multiple
  // `hiddenInPlanList` filter add-ons. Simpler model: don't emit them
  // at all. They are flight-director instructions on the published
  // plate, not navigation waypoints; the plate itself depicts the
  // climb geometry, and the flight plan should connect real fixes.
  // The missed-approach propagation heuristic that previously sniffed
  // for these CIFP legtypes in `out[]` now sees them via the source
  // procedure data instead — see `legTypeFromProc` lookups in the
  // post-pass. 2026-05-17.
  if (isAltitudeTerminatorLeg(leg)) {
    return null;
  }

  // Manual-terminator legs with a named fix but no coords — keep as a
  // visible placeholder so the pilot sees the labeled fix in the list,
  // but mark placeholder so distance math skips the (0, 0) coords.
  if (leg.fixIdent && (leg.fixLat == null || leg.fixLon == null)) {
    return {
      ident: leg.fixIdent,
      lat: 0,
      lon: 0,
      kind: 'procedure',
      procedureTag: { ...baseTag, placeholder: true },
    };
  }

  return null;
}

// ── PlanStore integration helpers ────────────────────────────────────────

/**
 * Splice procedure points into the route at the correct location:
 *   SID   : just after the departure airport
 *   STAR  : just before the destination airport
 *   IAP   : just after the destination airport
 *
 * If the route doesn't contain the relevant anchor airport, the
 * procedure is appended to the end.
 *
 * Replaces any existing block of points that already came from the
 * same procedure (so the user can switch transitions without
 * accumulating cruft).
 */
export function spliceProcedureIntoRoute(
  route: ExtendedPlanPoint[],
  expanded: ExtendedPlanPoint[],
  airportIcao: string,
  routeType: 'SID' | 'STAR' | 'IAP',
): ExtendedPlanPoint[] {
  // Drop any existing points that came from the same routeType at this
  // airport. We match LIBERALLY on airport identity so that mismatched
  // FAA vs ICAO idents (e.g. plan row "O46" vs procedureTag.airportIcao
  // "KO46") still dedup correctly — the previous strict equality let
  // duplicate FOBRO1 blocks accumulate when the call argument differed
  // from the procedureTag's airportIcao field (Bug #3, 2026-05-16).
  const cleaned = route.filter((p) => {
    const tag = p.procedureTag;
    if (!tag) return true;
    if (tag.routeType !== routeType) return true;
    if (sameAirportIcao(tag.airportIcao, airportIcao)) return false;
    return true;
  });

  if (expanded.length === 0) return cleaned;

  // Find the anchor airport's index.
  let anchorIdx = -1;
  if (routeType === 'SID') {
    // Anchor is the FIRST airport row in the route.
    anchorIdx = cleaned.findIndex((p) => p.kind === 'airport' && sameAirportIcao(p.ident, airportIcao));
    if (anchorIdx < 0) anchorIdx = cleaned.findIndex((p) => p.kind === 'airport');
  } else {
    // STAR / IAP: anchor is the LAST airport row matching the dest.
    for (let i = cleaned.length - 1; i >= 0; i--) {
      if (cleaned[i].kind === 'airport' && sameAirportIcao(cleaned[i].ident, airportIcao)) { anchorIdx = i; break; }
    }
    if (anchorIdx < 0) {
      for (let i = cleaned.length - 1; i >= 0; i--) {
        if (cleaned[i].kind === 'airport') { anchorIdx = i; break; }
      }
    }
  }

  if (anchorIdx < 0) return [...cleaned, ...expanded];

  if (routeType === 'SID') {
    // Insert AFTER the anchor.
    return [
      ...cleaned.slice(0, anchorIdx + 1),
      ...expanded,
      ...cleaned.slice(anchorIdx + 1),
    ];
  }
  if (routeType === 'STAR') {
    // Insert BEFORE the destination anchor.
    return [
      ...cleaned.slice(0, anchorIdx),
      ...expanded,
      ...cleaned.slice(anchorIdx),
    ];
  }
  if (routeType === 'IAP') {
    // IAP: split into inbound + missed-approach halves. The destination
    // airport row sits BETWEEN the two halves so the route reads:
    //   ... → IAP inbound fixes → DEST → missed-approach legs.
    // Pilots fly the inbound to landing, and the missed-approach is the
    // alternate sequence executed from the MAP. Putting the destination
    // in the middle matches Garmin G1000 / ForeFlight behavior and lets
    // the active-leg logic stop at the destination on the inbound
    // sequence by default. See Bug #8 (2026-05-16).
    const inbound: ExtendedPlanPoint[] = [];
    const missed:  ExtendedPlanPoint[] = [];
    for (const p of expanded) {
      if (p.procedureTag?.isMissedApproach) missed.push(p);
      else inbound.push(p);
    }
    return [
      ...cleaned.slice(0, anchorIdx),
      ...inbound,
      cleaned[anchorIdx],            // destination airport row
      ...missed,
      ...cleaned.slice(anchorIdx + 1),
    ];
  }
  return [...cleaned, ...expanded];
}

/** Match airport identifiers liberally so 'O46' and 'KO46' compare equal.
 *  Strips a leading 'K' on 4-character US idents to bridge the FAA-vs-ICAO
 *  naming. Exported for callers (ProceduresSection.tsx) that look up the
 *  "currently-selected procedure for this airport" out of the route. */
export function sameAirportIcao(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const norm = (s: string): string => (s.length === 4 && s.startsWith('K') ? s.slice(1) : s);
  return norm(a) === norm(b);
}
